import { z } from 'zod';
import type { IsoDate } from './dates';
import { ABSENCE_CODES, DAY_KINDS, type AbsenceCode, type DayKind } from './enums';
import { idSchema, isoDateSchema, optionalText } from './schemas';
import { isActiveOn } from './staffing';

// ───────────── Правило 12.4.3: виробничий календар ─────────────

export interface CalendarDay {
  date: IsoDate;
  kind: DayKind;
  hours: number;
  note: string | null;
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** Православний Великдень (юліанський пасхалій, алгоритм Меєуса) у григоріанському календарі. */
export function orthodoxEaster(year: number): IsoDate {
  const a = year % 4;
  const b = year % 7;
  const c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31);
  const day = ((d + e + 114) % 31) + 1;
  // Юліанська дата → григоріанська (+13 днів для 1900–2099).
  const date = new Date(Date.UTC(year, month - 1, day + 13));
  return date.toISOString().slice(0, 10);
}

export function addDaysIso(date: IsoDate, days: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Державні свята України (ст. 73 КЗпП у редакції 2023 року). */
export function ukrainianHolidays(year: number): { date: IsoDate; name: string }[] {
  const easter = orthodoxEaster(year);
  return [
    { date: iso(year, 1, 1), name: 'Новий рік' },
    { date: iso(year, 3, 8), name: 'Міжнародний жіночий день' },
    { date: easter, name: 'Великдень' },
    { date: addDaysIso(easter, 49), name: 'Трійця' },
    { date: iso(year, 5, 1), name: 'День праці' },
    { date: iso(year, 5, 8), name: 'День пам’яті та перемоги над нацизмом' },
    { date: iso(year, 6, 28), name: 'День Конституції України' },
    { date: iso(year, 7, 15), name: 'День Української Державності' },
    { date: iso(year, 8, 24), name: 'День Незалежності України' },
    { date: iso(year, 10, 1), name: 'День захисників і захисниць України' },
    { date: iso(year, 12, 25), name: 'Різдво Христове' },
  ].sort((x, y) => x.date.localeCompare(y.date));
}

export interface GenerateOptions {
  hoursPerDay: number;
  /**
   * Чи є свята вихідними. Під час воєнного стану (з 24.02.2022) свята — робочі дні
   * (ст. 73 КЗпП не застосовується), тому за замовчуванням false.
   */
  holidaysAreDaysOff: boolean;
}

/**
 * Календар на рік: субота й неділя — вихідні; свята — вихідні або робочі з приміткою;
 * переддень вихідного свята — скорочений на 1 годину.
 */
export function generateYear(year: number, opts: GenerateOptions): CalendarDay[] {
  const holidays = new Map(ukrainianHolidays(year).map((h) => [h.date, h.name]));
  const days: CalendarDay[] = [];
  for (
    let d = new Date(Date.UTC(year, 0, 1));
    d.getUTCFullYear() === year;
    d.setUTCDate(d.getUTCDate() + 1)
  ) {
    const date = d.toISOString().slice(0, 10);
    const weekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
    const holiday = holidays.get(date) ?? null;
    if (weekend) {
      days.push({ date, kind: 'weekend', hours: 0, note: holiday });
    } else if (holiday && opts.holidaysAreDaysOff) {
      days.push({ date, kind: 'holiday', hours: 0, note: holiday });
    } else {
      days.push({ date, kind: 'working', hours: opts.hoursPerDay, note: holiday });
    }
  }
  if (opts.holidaysAreDaysOff) {
    for (let i = 0; i < days.length - 1; i++) {
      const next = days[i + 1]!;
      if (days[i]!.kind === 'working' && next.kind === 'holiday') {
        days[i] = { ...days[i]!, kind: 'short', hours: Math.max(0, opts.hoursPerDay - 1) };
      }
    }
  }
  return days;
}

/** Дні місяця `yyyy-MM`. */
export function monthDays(month: string): IsoDate[] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => iso(y, m, i + 1));
}

export const monthStart = (month: string): IsoDate => `${month}-01`;
export const monthEnd = (month: string): IsoDate => monthDays(month).at(-1)!;

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

/** Планові години за період з урахуванням FTE і періоду призначення. */
export function plannedHours(
  days: CalendarDay[],
  assignment: { fte: number; startsAt: IsoDate; endsAt: IsoDate | null },
): number {
  return round2(
    days
      .filter((d) => (d.kind === 'working' || d.kind === 'short') && isActiveOn(assignment, d.date))
      .reduce((s, d) => s + d.hours * assignment.fte, 0),
  );
}

export interface TimesheetEntryInput {
  date: IsoDate;
  projectId: string | null;
  hours: number;
  absence: AbsenceCode | null;
}

/**
 * Табель «за планом»: на кожен робочий день — години кожного активного призначення
 * (норма дня × FTE), з точністю до чверті години.
 */
export function planTimesheet(
  days: CalendarDay[],
  assignments: { projectId: string; fte: number; startsAt: IsoDate; endsAt: IsoDate | null }[],
): TimesheetEntryInput[] {
  const entries: TimesheetEntryInput[] = [];
  for (const d of days) {
    if (d.kind !== 'working' && d.kind !== 'short') continue;
    const byProject = new Map<string, number>();
    for (const a of assignments) {
      if (!isActiveOn(a, d.date)) continue;
      byProject.set(a.projectId, (byProject.get(a.projectId) ?? 0) + d.hours * a.fte);
    }
    for (const [projectId, h] of byProject) {
      entries.push({ date: d.date, projectId, hours: Math.round(h * 4) / 4, absence: null });
    }
  }
  return entries;
}

export interface TimesheetIssue {
  date: IsoDate;
  code: 'over_24h' | 'hours_on_absence' | 'weekend_work' | 'outside_assignment' | 'over_day_norm';
}

/** Перевірки табеля: помилки (over_24h, hours_on_absence) і попередження (решта). */
export function checkTimesheet(
  days: CalendarDay[],
  entries: TimesheetEntryInput[],
  assignments: { projectId: string; fte: number; startsAt: IsoDate; endsAt: IsoDate | null }[],
): TimesheetIssue[] {
  const issues: TimesheetIssue[] = [];
  const dayMap = new Map(days.map((d) => [d.date, d]));
  const byDate = new Map<IsoDate, TimesheetEntryInput[]>();
  for (const e of entries) byDate.set(e.date, [...(byDate.get(e.date) ?? []), e]);
  for (const [date, list] of byDate) {
    const work = list.filter((e) => !e.absence);
    const total = work.reduce((s, e) => s + e.hours, 0);
    if (total > 24) issues.push({ date, code: 'over_24h' });
    if (list.some((e) => e.absence) && total > 0) issues.push({ date, code: 'hours_on_absence' });
    const day = dayMap.get(date);
    if (day && (day.kind === 'weekend' || day.kind === 'holiday') && total > 0) {
      issues.push({ date, code: 'weekend_work' });
    }
    if (day && total > 0 && total > day.hours && day.hours > 0)
      issues.push({ date, code: 'over_day_norm' });
    for (const e of work) {
      if (
        e.hours > 0 &&
        !assignments.some((a) => a.projectId === e.projectId && isActiveOn(a, date))
      ) {
        issues.push({ date, code: 'outside_assignment' });
        break;
      }
    }
  }
  return issues.sort((a, b) => a.date.localeCompare(b.date));
}

export const BLOCKING_TIMESHEET_ISSUES: TimesheetIssue['code'][] = ['over_24h', 'hours_on_absence'];

// ───────────── Схеми ─────────────

const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Місяць у форматі РРРР-ММ');
export { monthSchema };

export const calendarGenerateSchema = z.object({
  year: z.number().int().min(2020).max(2100),
  hoursPerDay: z.number().min(1).max(12).default(8),
  holidaysAreDaysOff: z.boolean().default(false),
  /** true — перезаписати вже змінені вручну дні. */
  overwrite: z.boolean().default(false),
});

export const calendarDaySchema = z.object({
  date: isoDateSchema,
  kind: z.enum(DAY_KINDS),
  hours: z.number().min(0).max(24),
  note: optionalText(200),
});

const hours = z
  .number()
  .min(0)
  .max(24)
  .transform((v) => Math.round(v * 100) / 100);

export const timesheetEntrySchema = z.object({
  date: isoDateSchema,
  projectId: idSchema.nullable(),
  hours,
  absence: z.enum(ABSENCE_CODES).nullable(),
});

export const timesheetSaveSchema = z.object({
  employeeId: idSchema,
  month: monthSchema,
  entries: z.array(timesheetEntrySchema).max(31 * 10),
  expectedUpdatedAt: z.coerce.date().optional(),
});
export type TimesheetSaveInput = z.input<typeof timesheetSaveSchema>;

export const timesheetRefSchema = z.object({ employeeId: idSchema, month: monthSchema });
export const timesheetReturnSchema = timesheetRefSchema.extend({
  comment: z.string().trim().min(1, 'Вкажіть причину').max(2000),
});
